// Daily-puzzle selection.
//
// SPEC §4 originally said `corpus[dayOffset % corpus.length]`. As of the
// `letter-flick-daily-puzzles-v1.0` corpus, each puzzle carries its own
// `letter_flick_puzzle_date` and the web does a DATE-KEYED lookup: today's
// local date (YYYY-MM-DD) is matched against each puzzle's date string.
// Today < first → puzzles[0]. Today > last → puzzles[last]. Today in a gap
// → closest by absolute day difference, tie broken later-date-wins.
//
// Local-date boundary matches Wordle precedent (early-timezone players get
// the share advantage; "today" matches the player's wall calendar).
//
// `formatLocalDate` is exported because the persistence layer's day-rollover
// detector compares stored `todayLocalDate` against today's formatted string;
// keeping a single formatter prevents drift between predicate and writer.

import { CORPUS, type CorpusPuzzle } from './corpus'

// EPOCH = 2026-01-01. Anchoring at year-start means by the time the game
// launches mid-year, the streak math has a stable origin. `getDayOffset` is
// still used by stats.ts for streak / missed-day detection; the daily
// selector itself no longer uses it (date-keyed lookup replaces modulo).
export const EPOCH_YEAR = 2026 as const
export const EPOCH_MONTH = 1 as const // 1-indexed (human convention)
export const EPOCH_DAY = 1 as const

const MS_PER_DAY = 86_400_000

export function getDayOffset(now: Date): number {
  const nowMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const epochMidnight = new Date(EPOCH_YEAR, EPOCH_MONTH - 1, EPOCH_DAY)
  const diffMs = nowMidnight.getTime() - epochMidnight.getTime()
  return Math.round(diffMs / MS_PER_DAY)
}

// Format a Date as 'YYYY-MM-DD' in the player's local timezone. Used both as
// the puzzle-lookup key AND as the persisted `todayLocalDate` value, so the
// rollover predicate's `persisted.todayLocalDate !== formatLocalDate(...)`
// comparison can't drift between writer and reader.
export function formatLocalDate(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${String(y)}-${m}-${day}`
}

// Internal — parses 'YYYY-MM-DD' as local-midnight. Critical: do NOT use
// `new Date(s)` here. Date.parse treats bare 'YYYY-MM-DD' as UTC in most
// engines, which skews by ±1 day for players in negative-offset timezones
// when we later subtract two such Date instances. Constructing via the
// (year, month, day) signature builds local-midnight unambiguously and
// round-trips with formatLocalDate.
function parseLocalDate(s: string): Date {
  const y = Number(s.slice(0, 4))
  const m = Number(s.slice(5, 7))
  const d = Number(s.slice(8, 10))
  return new Date(y, m - 1, d)
}

// Resolve today's date to a corpus array-index. Three-stage cascade:
//   1. Exact-match: today's date appears in the corpus → that puzzle.
//   2. Clamp: today is before the first or after the last puzzle's date →
//      first/last puzzle respectively.
//   3. Gap: today falls between two corpus dates → closest by absolute
//      day-difference. Ties (today equidistant between two adjacent corpus
//      dates) break later-date-wins, since the corpus is sorted ascending
//      and we use `<=` so the later index overwrites on equality.
// Throws on empty corpus (the only loud failure mode this layer can have).
export function getDailyPuzzleArrayIndex(now?: Date): number {
  const puzzles = CORPUS.puzzles
  if (puzzles.length === 0) {
    throw new Error('Cannot select daily puzzle from empty corpus')
  }
  const todayStr = formatLocalDate(now ?? new Date())

  // 1. Exact match — fast path. ISO date strings compare lex-equally so a
  //    string === string compare is correct.
  for (let i = 0; i < puzzles.length; i += 1) {
    const p = puzzles[i]
    if (p === undefined) continue
    if (p.letter_flick_puzzle_date === todayStr) return i
  }

  // 2. Clamp — before first or after last. ISO 'YYYY-MM-DD' is lex-sortable.
  const firstP = puzzles[0]
  const lastP = puzzles[puzzles.length - 1]
  if (firstP === undefined || lastP === undefined) {
    throw new Error('Corpus malformed: missing first/last puzzle')
  }
  if (todayStr < firstP.letter_flick_puzzle_date) return 0
  if (todayStr > lastP.letter_flick_puzzle_date) return puzzles.length - 1

  // 3. Gap — closest by absolute day-diff. `<=` keeps the later index on a
  //    tie, which the corpus's ascending date sort means is the later date.
  const todayMs = parseLocalDate(todayStr).getTime()
  let bestIdx = 0
  let bestDiff = Number.POSITIVE_INFINITY
  for (let i = 0; i < puzzles.length; i += 1) {
    const p = puzzles[i]
    if (p === undefined) continue
    const pMs = parseLocalDate(p.letter_flick_puzzle_date).getTime()
    const diff = Math.abs(Math.round((pMs - todayMs) / MS_PER_DAY))
    if (diff <= bestDiff) {
      bestIdx = i
      bestDiff = diff
    }
  }
  return bestIdx
}

// Returns the CorpusPuzzle (wire shape with date + number metadata) selected
// for today. Used by main.ts to read the display number alongside the
// playable puzzle without re-running the date-lookup twice.
export function getDailyPuzzleEntry(now?: Date): CorpusPuzzle {
  const idx = getDailyPuzzleArrayIndex(now)
  const entry = CORPUS.puzzles[idx]
  if (entry === undefined) {
    // Unreachable: getDailyPuzzleArrayIndex guarantees idx ∈ [0, len).
    // But noUncheckedIndexedAccess requires the narrow; we never use `!`.
    throw new Error(
      `Daily puzzle index ${String(idx)} out of range (corpus has ${String(CORPUS.puzzles.length)} puzzles)`,
    )
  }
  return entry
}

// Display puzzle number for the share-grid header ("Letter Flick #207").
// Now read from corpus data — no longer guaranteed to equal `dayOffset + 1`
// since the curator assigns the display number independently from the day
// offset.
export function getPuzzleNumber(now?: Date): number {
  return getDailyPuzzleEntry(now).letter_flick_puzzle_number
}

// The label shown once the countdown has reached its target (the puzzle day's
// rollover boundary has passed). The endgame modal swaps to this instead of a
// negative/zeroed timer; closing the modal then applies the deferred rollover.
export const COUNTDOWN_READY_LABEL = 'Next Letter Flick is ready.'

// The fixed local-midnight instant at which a given puzzle-day rolls over — the
// next local midnight AFTER `puzzleDate` ('YYYY-MM-DD'). This is a FIXED target,
// not "tomorrow relative to now": the endgame countdown ticks down toward it and
// reaches zero, rather than re-targeting the next day on every call (which could
// never hit zero). `new Date(y, m, d)` normalises month/day overflow, so the
// last day of a month rolls correctly into the first of the next.
export function endOfPuzzleDay(puzzleDate: string): Date {
  const parts = puzzleDate.split('-')
  const y = Number(parts[0])
  const m = Number(parts[1])
  const d = Number(parts[2])
  return new Date(y, m - 1, d + 1)
}

// Live countdown label for the end-of-game modal footer, counting down to a
// FIXED `target` instant (see endOfPuzzleDay). Renders whole h/m/s
// ("Next Letter Flick in 7h 22m 09s"); once `now` reaches or passes `target`,
// renders COUNTDOWN_READY_LABEL. Recomputed from the real clock on every tick,
// so it never accumulates drift.
export function getCountdownLabel(target: Date, now?: Date): string {
  const n = now ?? new Date()
  const diffMs = target.getTime() - n.getTime()
  if (diffMs <= 0) {
    return COUNTDOWN_READY_LABEL
  }
  const totalSeconds = Math.floor(diffMs / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  // Zero-pad to two digits so the label's character count never changes as it
  // counts down (paired with `font-variant-numeric: tabular-nums` on
  // .modal__countdown so each digit is the same width — no horizontal jitter).
  const hh = String(hours).padStart(2, '0')
  const mm = String(minutes).padStart(2, '0')
  const ss = String(seconds).padStart(2, '0')
  return `Next Letter Flick in ${hh}:${mm}:${ss}`
}
